// @ts-ignore
import { fetch as nativeFetch } from "xhook";
import { ContentsManager } from "./contents/manager";
import { getContext } from "./context";
import { HTTPError } from "./httpError";

const DUMMY_BASE_URL = "https://www.kaggle.com";

// https://github.com/jupyter/notebook/blob/master/notebook/base/handlers.py#L583
export abstract class APIHandler {
  private response: Response | null = null;
  private readonly headers: Headers;
  private status = 200;

  constructor(
    protected readonly request: Request,
    protected readonly contentsManager: ContentsManager
  ) {
    this.headers = new Headers();
  }

  getQueryArguement(name: string, def: string | null) {
    return (
      // URL is just a convenience object to access search params.
      new URL(this.request.url, DUMMY_BASE_URL).searchParams.get(name) || def
    );
  }

  locationUrl(path: string) {
    return path;
  }

  setHeader(name: string, value: string) {
    this.headers.append(name, value);
  }

  setStatus(status: number) {
    this.status = status;
  }

  finish(body: string | {} | null) {
    this.response = new Response(this.getStringBody(body), {
      headers: this.headers,
      status: this.status,
    });
  }

  finishResponse(response: Response) {
    this.response = response;
  }

  async proxyRequest() {
    const { jupyterProxyUrl, normalizedPath } = getContext();
    if (!jupyterProxyUrl) {
      throw new Error("Attempted to proxy request without a proxy URL");
    }
    const url = `${jupyterProxyUrl}${normalizedPath}`;
    let body;
    switch (this.request.method) {
      case "POST":
      case "PATCH":
      case "PUT":
      case "DELETE":
        body = await this.getRequestStringBody();
        break;
      case "GET":
        body = null;
    }
    const response = await nativeFetch(url, {
      body,
      method: this.request.method,
    });
    this.finishResponse(response);
  }

  private getStringBody(body: string | {} | null) {
    return body === null
      ? null
      : typeof body === "string"
      ? body
      : JSON.stringify(body);
  }

  private async getRequestStringBody() {
    try {
      if (this.request.text) {
        return await this.request.text();
      }
      // Otherwise the text() isn't defined on request (ex TypeError: _this.request.text is not a function)
      // fallback to request.body.
    } catch {
      // text() failed to run, fallback to request.body.
    }
    if (!this.request.body) {
      return null;
    }
    return Promise.resolve(this.request.body.toString());
  }

  async handle(params: {}) {
    try {
      switch (this.request.method) {
        case "GET":
          await this.get(params);
          break;
        case "POST":
          await this.post(params);
          break;
        case "PUT":
          await this.put(params);
          break;
        case "PATCH":
          await this.patch(params);
          break;
        case "DELETE":
          await this.delete(params);
          break;
        default:
          throw new Error(`HTTP method '${this.request.method}' not supported`);
      }
      return this.response;
    } catch (error) {
      return new Response(error?.message || error, {
        status: error?.status || 500,
      });
    }
  }

  async get(_params: {}): Promise<any> {
    throw new HTTPError(500, "'GET' not implemented for this handler");
  }

  async post(_params: {}): Promise<any> {
    throw new HTTPError(500, "'POST' not implemented for this handler");
  }

  async put(_params: {}): Promise<any> {
    throw new HTTPError(500, "'PUT' not implemented for this handler");
  }

  async patch(_params: {}): Promise<any> {
    throw new HTTPError(500, "'PATCH' not implemented for this handler");
  }

  async delete(_params: {}): Promise<any> {
    throw new HTTPError(500, "'DELETE' not implemented for this handler");
  }
}
